import {
  Button,
  PageSectionContainer,
  ProductCard,
  ProductFilterTags,
  useDrawer,
} from '../index';
import {Product} from '@shopify/hydrogen/storefront-api-types';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {ProductFilter} from './ProductFilter.client';
import {ProductFilterDrawer} from './ProductFilterDrawer.client';
import {ContentCardEntity} from '~/types/CollectionEntity';
import {ProductListContentCard} from './ProductListContentCard';
import {ProductListHeading} from './ProductListHeading.client';
import {
  ANALYTIC_EVENT_NAMES,
  BOOST_DEFAULT_PAGE_SIZE,
  BOOST_DEFAULT_PARAMS,
  BOOST_FILTER_PREFIX,
  BOOST_PRODUCT_BASE_URL,
  BOOST_REQUEST_HIDE_TAG,
  BOOST_VARIANT_AVAILABLE_OPTION,
  DATA_TEST_IDS,
  SORT_KEYS,
  SORT_OPTIONS,
} from '~/lib/const';
import {useFilterOption} from '~/hooks/useFilterOption';
import {
  FilterStateType,
  CollectionBoostNormalized,
  FilterOption,
  ProductColorEntity,
} from '~/types';
import normalizeCollectionData from '~/lib/normalize/normalizeCollectionData';
import {ClientAnalytics, useNavigate} from '@shopify/hydrogen';
import {getNumberID, parseFilterToSearchParams} from '~/lib/utils';

function getSearchParamsFromFilterState(
  filterState: FilterStateType,
  func: (key: string, value: string | string[]) => void,
) {
  return Object.entries(filterState).map(([filterKey, filterValue]) => {
    let value;

    if (
      typeof filterValue === 'object' &&
      !Array.isArray(filterValue) &&
      filterValue != null
    ) {
      value = `${filterValue.min}:${filterValue.max}`;
    } else {
      value = filterValue as string | string[];
    }

    const key = `${filterKey}[]`;

    if (typeof func === 'function') {
      func(key, value);
    } else {
      return [key, value];
    }
  });
}

// If `query` param is provided, "search" api is used instead of "filter" api. Refer to fetchProducts function below.
export function ProductList({
  sortKey,
  filters,
  query,
  collection,
  contentCards = [],
  total: totalDefault,
  collectionConfig,
  colors,
}: {
  className?: string;
  sortKey: string;
  total?: number;
  query?: string;
  filters: FilterStateType;
  collection: {id?: string; title: string};
  collectionConfig: CollectionBoostNormalized;
  contentCards?: ContentCardEntity[];
  colors: ProductColorEntity[];
}) {
  const navigate = useNavigate();
  const firstLoad = useRef(false);

  const [filterOptionsState, setFilterOptions] = useState(
    collectionConfig.filter.options,
  );
  const [total, setTotal] = useState(totalDefault);
  const [sort, setSort] = useState(sortKey ?? SORT_OPTIONS[0].value);
  const [hideFilters, setHideFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState(collectionConfig.products);
  const [filterApplied, setFilterApplied] = useState(filters);
  const {isOpen, openDrawer, closeDrawer} = useDrawer();
  const controllerRef = useRef<AbortController | undefined>();
  // TODO: show loading indicator
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);

  const collectionIdRaw = useMemo(() => {
    return collection.id?.split('/')?.pop();
  }, [collection.id]);
  const filterOptions = useFilterOption(filterOptionsState, colors ?? []);
  const filterOptionsById = useMemo(
    () =>
      filterOptions.reduce((obj, item) => {
        obj[item.filterOptionId] = {name: item.name};
        return obj;
      }, {} as {[key: string]: {name: string}}),
    [filterOptions],
  );
  const filterOptionsRef = useRef(filterOptions);
  filterOptionsRef.current = filterOptions;

  const fetchProductsCallback = useCallback(
    async (
      sort: string,
      page: number,
      filters: FilterStateType,
      collectionId?: string,
      query?: string,
    ) => {
      try {
        setLoading(true);
        const data = await fetchProducts({
          sort,
          filterOptions: filterOptionsRef.current,
          filterApplied: filters,
          controllerRef,
          page,
          limit: BOOST_DEFAULT_PAGE_SIZE,
          collectionId,
          query,
        });
        setTotal(data.totalProduct);
        setProducts((prev) =>
          page === 1
            ? [...(data?.products ?? [])]
            : [...prev, ...(data?.products ?? [])],
        );
        setFilterOptions(data.filter.options);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    // Dont fetch on first render because first render comes from server already
    if (firstLoad.current) {
      const searchParams = new URLSearchParams(window.location.search);
      const sortValue = searchParams.get('sort') ?? SORT_OPTIONS[0].value;
      // If collection id is null this means search api will be used
      fetchProductsCallback(
        sortValue,
        page,
        filterApplied,
        collectionIdRaw,
        query,
      );
    } else {
      firstLoad.current = true;
    }
  }, [collectionIdRaw, page, filterApplied, fetchProductsCallback, query]);

  const items = useMemo(() => {
    const list: Array<
      | {
          type: 'product';
          product: Product;
        }
      | {
          type: 'contentCard';
          contentCard: ContentCardEntity;
        }
    > = [];

    const cards = Object.keys(filterApplied).length > 0 ? [] : contentCards;

    let addedContentCardCount = 0;
    for (let index = 0; index < products.length; index++) {
      const product = {
        ...products[index],
        // This is due to ProductCard works with this structure
        variants: {
          nodes: products[index].variants,
        },
      };

      const contentCard = cards.find((card) => (card.order ?? 0) - 1 === index);

      if (contentCard) {
        list.push({
          type: 'contentCard',
          contentCard,
        });
        addedContentCardCount++;
      }

      list.push({
        type: 'product',
        // @ts-ignore
        product,
      });
    }

    // Add the remaining cards to end of the list if there is any
    for (let i = addedContentCardCount; i < cards.length; i++) {
      list.push({
        type: 'contentCard',
        contentCard: cards[i],
      });
      addedContentCardCount++;
    }

    return list;
  }, [filterApplied, contentCards, products]);

  const handleClickApplyFilters = useCallback(
    (filterState: FilterStateType) => {
      const url = new URL(location.href);
      getSearchParamsFromFilterState(filterState, (key, value) => {
        if (value == null || (Array.isArray(value) && value.length === 0)) {
          url.searchParams.delete(key);
        } else {
          url.searchParams.set(key, String(value));
        }
      });
      navigate(location.pathname + `?${url.searchParams.toString()}`, {
        replace: true,
        scroll: false,
      });
      setFilterApplied({...filterState});
      setPage(1);
    },
    [navigate],
  );

  const handleClickClearFilters = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('sort', sort);
    // Filters start with `pf_`
    const values = [...searchParams];
    const searchParamsWithoutFilters = values.filter(
      ([k, v]) => !k.startsWith(BOOST_FILTER_PREFIX),
    );
    const searchParamsUpdated = new URLSearchParams(searchParamsWithoutFilters);
    navigate(location.pathname + `?${searchParamsUpdated.toString()}`, {
      replace: true,
      scroll: false,
    });
    setFilterApplied({});
    setPage(1);
  }, [sort, navigate]);

  const handleClickRemoveFilters = useCallback(
    (key: string, value: string) => {
      const currentValue = filterApplied[key];
      const newValue = Array.isArray(currentValue)
        ? // @ts-ignore
          currentValue.filter((v: string) => {
            const valueNormalized =
              typeof v === 'string' && v.includes(':') ? v.split(':')[1] : v;
            return valueNormalized !== value;
          })
        : undefined;
      handleClickApplyFilters({...filterApplied, [key]: newValue});
    },
    [filterApplied, handleClickApplyFilters],
  );

  const sortCallback = useCallback(
    (value: string) => {
      const url = new URL(location.href);
      url.searchParams.set('sort', value);
      navigate(url.toString(), {
        replace: true,
        scroll: false,
      });
      setPage(1);
      setSort(value);
      fetchProductsCallback(value, 1, filterApplied, collectionIdRaw, query);
    },
    [collectionIdRaw, fetchProductsCallback, filterApplied, navigate, query],
  );

  useEffect(() => {
    const events: any[] = [
      {
        _clear: true,
        event: ANALYTIC_EVENT_NAMES.VIEW_ITEM_LIST,
        ecommerce: {
          item_list_name: collection.title,
          items: products.map((product, index) => ({
            item_name: product.title,
            item_id: getNumberID(product.id),
            item_brand: product.vendor,
            currency: 'USD',
            price: product.variants?.[0]?.priceV2?.amount ?? 0,
            index: index.toString(),
            item_category: product.productType,
            item_variant: product.variants?.[0]?.title ?? product.title,
          })),
        },
      },
    ];

    if (query) {
      events.push({
        _clear: true,
        event: ANALYTIC_EVENT_NAMES.VIEW_SEARCH_RESULTS,
        search: {
          search_term: query,
          search_result_count: total ?? 0,
        },
      });
    }

    ClientAnalytics.publish(ANALYTIC_EVENT_NAMES.VIEW_ITEM_LIST, true, {
      events,
    });
  }, [collection.title, query, products, total]);

  return (
    <PageSectionContainer
      disableMarginY
      className="mt-[35px] mb-[90px] 3xl:max-w-[2560px] 3xl:px-[4rem]"
    >
      <ProductFilterDrawer
        sort={sort}
        isOpen={isOpen}
        onClose={closeDrawer}
        title={collection.title.toUpperCase()}
        filterApplied={filterApplied}
        options={filterOptions}
        onClickApplyFilters={handleClickApplyFilters}
        onClickClearFilters={handleClickClearFilters}
        onChangeSort={sortCallback}
      />

      <div
        id="product-grid"
        className="flex items-center justify-between space-x-5"
      >
        <span className="text-22/30 xl:text-32/44 font-extrabold font-header">
          {collection.title.toUpperCase()} {total !== undefined && `(${total})`}
        </span>

        <ProductListHeading
          sort={sort}
          hideFilters={hideFilters}
          setHideFilters={setHideFilters}
          openDrawer={openDrawer}
          onChangeSort={sortCallback}
        />
      </div>

      <div className="flex mt-5 md:mt-[41px]">
        <div
          className={clsx('hidden xl:block w-[262px] shrink-0 transition-all', {
            '-ml-[262px] opacity-0':
              hideFilters || collectionConfig?.filter?.options == null,
          })}
        >
          {collectionConfig?.filter?.options && (
            <ProductFilter
              filterApplied={filterApplied}
              options={filterOptions}
              onClickApplyFilters={handleClickApplyFilters}
            />
          )}
        </div>

        <div
          className={clsx('w-full transition-all', {
            'xl:ml-[131px]': !hideFilters,
          })}
        >
          <div
            className={'mb-10 md:mb-[30px] min-h-[50px]'}
            style={{
              display: Object.keys(filterApplied).length > 0 ? 'block' : 'none',
            }}
          >
            <ProductFilterTags
              filters={filterApplied}
              filterOptionsById={filterOptionsById}
              onClickClearFilters={handleClickClearFilters}
              onClickRemoveFilter={handleClickRemoveFilters}
            />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-1">
            {items.map((item) => {
              if (item.type === 'product') {
                return (
                  <ProductCard product={item.product} key={item.product.id} />
                );
              } else {
                return (
                  <ProductListContentCard
                    key={item.contentCard.order}
                    contentCard={item.contentCard}
                  />
                );
              }
            })}
          </div>

          <div className="mt-[79px] flex flex-col items-center">
            <span
              data-test-length={DATA_TEST_IDS.PRODUCT_LENGTH(products.length)}
              data-test-total={DATA_TEST_IDS.PRODUCT_TOTAL(total)}
            >
              {total != undefined && total > 0
                ? `You've viewed ${products.length} of ${total} products`
                : 'No Results'}
            </span>

            {products.length < (total ?? Infinity) && (
              <Button
                data-testid={DATA_TEST_IDS.BUTTON('show-more')}
                variant="blue-secondary"
                size="md"
                className="mt-5"
                onClick={() => {
                  setPage((prev) => prev + 1);
                }}
              >
                SHOW MORE
              </Button>
            )}
          </div>
        </div>
      </div>
    </PageSectionContainer>
  );
}

async function fetchProducts({
  collectionId,
  sort,
  filterOptions,
  filterApplied,
  controllerRef,
  page,
  limit,
  query,
}: {
  sort: string;
  filterOptions: FilterOption[];
  filterApplied: FilterStateType;
  controllerRef: React.MutableRefObject<AbortController | undefined>;
  collectionId?: string;
  page?: number;
  limit?: number;
  query?: string;
}) {
  const params = new URLSearchParams(
    BOOST_DEFAULT_PARAMS + '&build_filter_tree=true&check_cache=true',
  );

  params.set('page', String(page ?? 1));
  params.set('limit', String(limit ?? BOOST_DEFAULT_PAGE_SIZE));
  if (sort.toLowerCase() !== SORT_KEYS.FEATURED.toLowerCase()) {
    params.set('sort', sort);
  }
  if (collectionId) {
    params.set('collection_scope', collectionId);
  }
  if (query) {
    params.set('q', query);
  }

  params.set('h_options', BOOST_REQUEST_HIDE_TAG);
  params.set(BOOST_VARIANT_AVAILABLE_OPTION, 'true');

  getSearchParamsFromFilterState(filterApplied, (key, value) => {
    parseFilterToSearchParams({
      params,
      filterOptions,
      key,
      value,
    });
  });

  if (controllerRef.current) {
    controllerRef.current.abort();
  }

  const controller = new AbortController();
  controllerRef.current = controller;

  const endpoint = query != null && query.length > 0 ? 'search' : 'filter';
  const urlString = `${BOOST_PRODUCT_BASE_URL}/bc-sf-filter/${endpoint}?${params.toString()}`;
  const response = await fetch(urlString, {
    signal: controllerRef.current?.signal,
  });
  const data = await response.json();
  const normalizeData = normalizeCollectionData(data);

  controllerRef.current = undefined;
  return normalizeData;
}
